/**
 * OwnLicenseRequiredModal - Shown when adding shared literature with a closed license
 *
 * The user must confirm their own license to use the material, as the original
 * license doesn't allow sharing/distribution.
 */

import { useState } from 'react';
import { Shield, AlertTriangle, X, Lock, Info } from 'lucide-react';
import InlineLicenseForm, {
  LicenseFormData,
  getDefaultLicenseFormData,
  LICENSE_TYPE_LABELS,
  CC_VARIANT_LABELS,
  LicenseType,
  CCVariant
} from './InlineLicenseForm';

interface OriginalLicenseInfo {
  license_type: LicenseType;
  cc_variant?: CCVariant | null;
  valid_until?: string;
  is_organization_specific?: boolean;
  max_users?: number;
  reason?: string;
}

interface SharedLiterature {
  id: string;
  title: string;
  author?: string;
  original_url?: string;
  publisher?: string;
}

interface OwnLicenseRequiredModalProps {
  literature: SharedLiterature;
  originalLicense: OriginalLicenseInfo | null;
  reason?: string;
  onConfirm: (ownLicense: LicenseFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export default function OwnLicenseRequiredModal({
  literature,
  originalLicense,
  reason,
  onConfirm,
  onCancel,
  isLoading = false
}: OwnLicenseRequiredModalProps) {
  const [licenseForm, setLicenseForm] = useState<LicenseFormData>(getDefaultLicenseFormData());
  const [licenseConfirmed, setLicenseConfirmed] = useState(false);

  const getReasonText = () => {
    if (reason) return reason;
    if (!originalLicense) return 'Ingen lisensinformasjon tilgjengelig for denne kilden.';

    if (originalLicense.is_organization_specific) {
      return 'Lisensen er begrenset til en spesifikk organisasjon.';
    }

    if (originalLicense.valid_until) {
      const expiryDate = new Date(originalLicense.valid_until);
      if (expiryDate < new Date()) {
        return `Original lisens utløp ${originalLicense.valid_until}.`;
      }
    }

    if (originalLicense.max_users) {
      return `Lisensen har en begrensning på ${originalLicense.max_users} brukere.`;
    }

    const typeLabel = LICENSE_TYPE_LABELS[originalLicense.license_type] || originalLicense.license_type;
    return `Lisenstypen "${typeLabel}" tillater ikke direkte deling.`;
  };

  const handleSubmit = () => {
    if (licenseConfirmed && licenseForm.license_type !== 'unknown') {
      onConfirm(licenseForm);
    }
  };

  const canSubmit = licenseConfirmed &&
    licenseForm.license_type !== 'unknown' &&
    (licenseForm.license_type !== 'creative_commons' || licenseForm.cc_variant);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Lock className="text-orange-600" size={24} />
            <h3 className="text-lg font-bold text-gray-900">Egen lisensbekreftelse påkrevd</h3>
          </div>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-gray-100 rounded-full transition"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Literature info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <h4 className="font-medium text-gray-900">{literature.title}</h4>
            {literature.author && (
              <p className="text-sm text-gray-600">Forfatter: {literature.author}</p>
            )}
            {literature.publisher && (
              <p className="text-sm text-gray-600">Utgiver: {literature.publisher}</p>
            )}
          </div>

          {/* Warning about why own license is needed */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-orange-800">
                  Denne litteraturen kan ikke arve eksisterende lisens
                </p>
                <p className="text-sm text-orange-700 mt-1">
                  {getReasonText()}
                </p>
                <p className="text-sm text-orange-700 mt-2">
                  Du må bekrefte at din organisasjon har egen tilgang til denne ressursen.
                </p>
              </div>
            </div>
          </div>

          {/* Original license info (if available) */}
          {originalLicense && originalLicense.license_type !== 'unknown' && (
            <div className="border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Info className="text-gray-500" size={16} />
                <span className="text-sm font-medium text-gray-700">Original lisens:</span>
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p>
                  Type: {LICENSE_TYPE_LABELS[originalLicense.license_type]}
                  {originalLicense.cc_variant && ` (${CC_VARIANT_LABELS[originalLicense.cc_variant] || originalLicense.cc_variant})`}
                </p>
                {originalLicense.valid_until && (
                  <p>Utløper: {originalLicense.valid_until}</p>
                )}
                {originalLicense.max_users && (
                  <p>Maks brukere: {originalLicense.max_users}</p>
                )}
              </div>
            </div>
          )}

          {/* Guidance */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Tips:</strong> Velg lisenstypen som beskriver hvordan din organisasjon
              har tilgang til denne litteraturen. For eksempel:
            </p>
            <ul className="text-sm text-blue-700 mt-2 space-y-1">
              <li>• <strong>Kjøpt lisens</strong> - hvis organisasjonen har kjøpt tilgang</li>
              <li>• <strong>Abonnement</strong> - hvis dere har abonnement på tjenesten</li>
              <li>• <strong>Kopinor-avtale</strong> - hvis dere har Kopinor-avtale som dekker bruken</li>
              <li>• <strong>Utdanningslisens</strong> - hvis dere har spesiell utdanningsavtale</li>
            </ul>
          </div>

          {/* License form */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Shield className="text-kf-primary" size={18} />
              Din organisasjons lisens
            </h4>
            <InlineLicenseForm
              value={licenseForm}
              onChange={setLicenseForm}
              confirmed={licenseConfirmed}
              onConfirmChange={setLicenseConfirmed}
              compact={false}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-100 transition"
          >
            Avbryt
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || isLoading}
            className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${
              canSubmit && !isLoading
                ? 'bg-kf-primary text-white hover:bg-kf-secondary'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <>
                <span className="animate-spin">⏳</span>
                Legger til...
              </>
            ) : (
              <>
                <Shield size={18} />
                Bekreft lisens og legg til
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
